import { atom, useAtom } from 'jotai'
import { useUserState } from '~/atoms/UserState'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  useGetMessageGroups,
  useMarkMessageGroupsAsRead,
} from '~/queries/messagingQueries'
import { MessagingEmitterEventType } from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'

import { MessageGroup } from '@mangovoice/types'

interface MessagingViewModelState {
  userMessageGroups?: MessageGroup[]
}

const messagingViewModelAtom = atom<MessagingViewModelState>({})
const GET_MESSAGE_GROUPS_CACHE_TIME_SECONDS = 30 * 1000

const logger = new Logger('MessageGroupsViewModelLogger')
export const useMessageGroupsViewModel = () => {
  const { pbxId } = useUserState()
  const [state, setState] = useAtom(messagingViewModelAtom)
  const setUserMessageGroups = (userMessageGroups: MessageGroup[]) => {
    setState((s) => {
      return {
        ...s,
        userMessageGroups,
      }
    })
  }
  const { subscribe: emitterSubscribe, emit: emitterEmit } = useEmitterService()

  const { refetch: getMessageGroups } = useGetMessageGroups({
    pbxId: pbxId ?? 0,
    options: {
      refetchOnWindowFocus: false,
      enabled: false,
      gcTime: GET_MESSAGE_GROUPS_CACHE_TIME_SECONDS,
      staleTime: GET_MESSAGE_GROUPS_CACHE_TIME_SECONDS,
    },
    onQuerySuccess: (data) => {
      const { results: newMessageGroups } = data
      setUserMessageGroups(newMessageGroups)
    },
  })

  const { mutate: markMessageGroupsAsRead } = useMarkMessageGroupsAsRead({
    onSuccess: (groupResults) => {
      const currentMessageGroups = [...(state.userMessageGroups ?? [])]
      for (const group of state.userMessageGroups ?? []) {
        const groupResult = groupResults.find((g) => g.groupId === group.id)
        if (groupResult) {
          group.unread_indicator_count = 0
        }
      }

      setUserMessageGroups(currentMessageGroups)
    },
  })

  /** On successful fetch of current groups, updates group state and marks all message groups as read - marking message groups as read will locally update all message groups with unread indicators as having 0 unread messages without performing a server-side refetch */
  const { refetch: getMessageGroupsAndMarkAsRead } = useGetMessageGroups({
    pbxId: pbxId,
    options: {
      refetchOnWindowFocus: false,
      enabled: false,
      gcTime: GET_MESSAGE_GROUPS_CACHE_TIME_SECONDS,
      staleTime: GET_MESSAGE_GROUPS_CACHE_TIME_SECONDS,
    },
    onQuerySuccess: (data) => {
      const { results: newMessageGroups } = data
      setUserMessageGroups(newMessageGroups)
      const ids = newMessageGroups
        .filter((messageGroup) => {
          return messageGroup.unread_indicator_count > 0
        })
        .map((messageGroup) => messageGroup.id)
      markMessageGroupsAsRead(ids)
      emitterEmit(MessagingEmitterEventType.MESSAGE_GROUPS_MARKED_READ, {
        messageGroupIds: ids,
      })
    },
  })

  /** Updates the current message group state from the server*/
  const tryFetchUserMessageGroups = () => {
    void getMessageGroups()
  }

  /** Marks all message groups as read */
  const markAllMessageGroupsAsRead = () => {
    void getMessageGroupsAndMarkAsRead()
  }

  //emitter subscriptions
  emitterSubscribe({
    subscriptionToken: 'MessageGroupsViewModel',
    subscription: {
      eventType: MessagingEmitterEventType.MESSAGE_THREAD_MARKED_READ,
      callback: (_) => {
        logger.debug(
          'Received EmitterEventType.MESSAGE_THREAD_MARKED_READ - refetching message groups',
        )
        void getMessageGroups()
      },
    },
  })

  return {
    messagingInteractionState: state,
    userMessageGroups: state.userMessageGroups,
    tryFetchUserMessageGroups,
    setUserMessageGroups,
    markAllMessageGroupsAsRead,
  }
}
